import axios from 'axios';

// Central place for the backend URL
export const API_URL =
  process.env.REACT_APP_API_URL ||
  'https://mwendwa-kitavi-family-tree-backend-production.up.railway.app';

const TOKEN_KEY = 'mm_token';
const USER_KEY = 'mm_user';

export const getToken = () => localStorage.getItem(TOKEN_KEY);

export const saveSession = (token, user) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

export const getSavedUser = () => {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY));
  } catch {
    return null;
  }
};

export const clearSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

// Attach the login token to EVERY axios request automatically.
axios.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// If the server genuinely rejects our session, log out cleanly — but only
// then. Previously ANY 401 (including ones from the login/reset endpoints, or
// a transient server hiccup) wiped the session and hard-reloaded, which is a
// big reason people felt "logged out every time". Now we ignore 401s from the
// auth endpoints themselves, and redirect once (no reload loop).
let loggingOut = false;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    const isAuthEndpoint = /\/api\/auth\/(login|forgot-password|reset-password|resend|verify|claim|newmember)/.test(url);
    if (status === 401 && getToken() && !isAuthEndpoint && !loggingOut) {
      loggingOut = true;
      clearSession();
      window.location.assign('/');
    }
    return Promise.reject(error);
  }
);

/* ================= Auth & registration wizard API ================= */

/**
 * login(identifier, password) — identifier is a phone number (0712...,
 * +254712...) or an email (kept for legacy accounts like the admin).
 */
export const login = async (identifier, password, accountId) => {
  const res = await axios.post(`${API_URL}/api/auth/login`, { identifier, password, accountId }, {
    // 300 = "choose which account" — a normal branch, not an error
    validateStatus: (status) => (status >= 200 && status < 400),
  });
  if (res.status === 300 && res.data.chooseAccount) {
    return { chooseAccount: true, accounts: res.data.accounts, message: res.data.message, identifier, password };
  }
  saveSession(res.data.token, res.data.user);
  return { user: res.data.user };
};

/** Start a password reset — sends a code to the phone (SMS) or email. */
export const forgotPassword = async (identifier) => {
  const res = await axios.post(`${API_URL}/api/auth/forgot-password`, { identifier });
  return res.data; // { message }
};

/**
 * Complete a password reset with the code + new password. On success the
 * member is signed straight in. If several accounts share the phone, returns
 * { chooseAccount, accounts } so the caller can ask which one.
 */
export const resetPassword = async (identifier, otp, password, accountId) => {
  const res = await axios.post(
    `${API_URL}/api/auth/reset-password`,
    { identifier, otp, password, accountId },
    { validateStatus: (status) => (status >= 200 && status < 400) }
  );
  if (res.status === 300 && res.data.chooseAccount) {
    return { chooseAccount: true, accounts: res.data.accounts, message: res.data.message };
  }
  saveSession(res.data.token, res.data.user);
  return { user: res.data.user };
};

/** Search the tree by name. gender optional ('FEMALE' for mother search). */
export const treeSearch = async (name, inviteCode, gender) => {
  const res = await axios.get(`${API_URL}/api/auth/tree-search`, {
    params: { name, code: inviteCode, gender: gender || undefined },
  });
  return res.data.results;
};

/** List spouses of a person (used for choosing a father after the mother). */
export const personSpouses = async (personId, inviteCode) => {
  const res = await axios.get(`${API_URL}/api/auth/person/${personId}/spouses`, {
    params: { code: inviteCode },
  });
  return res.data.spouses;
};

/** Start claiming an existing node. Returns { pendingId, message }. */
export const startClaim = async ({ personId, phone, email, inviteCode }) => {
  const res = await axios.post(`${API_URL}/api/auth/claim/start`, {
    personId,
    phone,
    email: email || undefined,
    inviteCode,
  });
  return res.data;
};

/** Start a new-member registration. Returns { pendingId, message }. */
export const startNewMember = async ({ name, gender, birthDate, motherId, fatherId, phone, email, inviteCode }) => {
  const res = await axios.post(`${API_URL}/api/auth/newmember/start`, {
    name,
    gender,
    birthDate: birthDate || undefined,
    motherId,
    fatherId: fatherId || undefined,
    phone,
    email: email || undefined,
    inviteCode,
  });
  return res.data;
};

/** Verify the OTP and set the password. Logs the new user in. */
export const verifyOtp = async ({ pendingId, otp, password }) => {
  const res = await axios.post(`${API_URL}/api/auth/verify`, { pendingId, otp, password });
  saveSession(res.data.token, res.data.user);
  return res.data.user;
};

/** Resend the OTP for a pending registration. */
export const resendOtp = async (pendingId, channel) => {
  const res = await axios.post(`${API_URL}/api/auth/resend`, { pendingId, channel });
  return res.data;
};

/** Add my spouse to my own node (member self-service, max 2). */
export const addMySpouse = async ({ name, gender, birthDate }) => {
  const res = await axios.post(`${API_URL}/api/family/spouse`, { name, gender, birthDate });
  return res.data;
};

export const logout = () => {
  clearSession();
  window.location.reload();
};

/** Change my own password (requires the current one). */
export const changePassword = async (currentPassword, newPassword) => {
  const res = await axios.post(`${API_URL}/api/auth/change-password`, { currentPassword, newPassword });
  return res.data;
};

/** Add or update the email on file (e.g. for accounts set up phone-only by the admin). */
export const updateEmail = async (email) => {
  const res = await axios.post(`${API_URL}/api/auth/update-email`, { email });
  return res.data;
};

export const getMe = async () => {
  const res = await axios.get(`${API_URL}/api/auth/me`);
  return res.data.user;
};

/* ================= Admin user management ================= */

export const getUsers = async () => {
  const res = await axios.get(`${API_URL}/api/auth/users`);
  return res.data;
};

export const setUserRole = async (userId, role) => {
  const res = await axios.put(`${API_URL}/api/auth/users/${userId}/role`, { role });
  return res.data;
};

export const deleteUser = async (userId) => {
  const res = await axios.delete(`${API_URL}/api/auth/users/${userId}`);
  return res.data;
};

/** Search the tree for a person by name, to set up their account on their behalf. */
export const adminSearchPeople = async (name) => {
  const res = await axios.get(`${API_URL}/api/auth/admin/search-people`, { params: { name } });
  return res.data.results;
};

/** Admin-assisted account creation — no OTP, admin verifies identity directly. */
export const adminCreateAccount = async ({ personId, phone, email, password }) => {
  const res = await axios.post(`${API_URL}/api/auth/admin/create-account`, { personId, phone, email, password });
  return res.data;
};

/* ================= Events & photos ================= */

export const listEvents = async () => {
  const res = await axios.get(`${API_URL}/api/events`);
  return res.data;
};

export const createEvent = async ({ title, type, date, location, description, targetAmount }) => {
  const res = await axios.post(`${API_URL}/api/events`, {
    title, type,
    date: date || undefined,
    location: location || undefined,
    description: description || undefined,
    targetAmount: targetAmount || undefined,
  });
  return res.data;
};

export const deleteEvent = async (eventId) => {
  const res = await axios.delete(`${API_URL}/api/events/${eventId}`);
  return res.data;
};

export const addPledge = async (eventId, amount, note) => {
  const res = await axios.post(`${API_URL}/api/events/${eventId}/pledges`, { amount, note });
  return res.data;
};

export const addComment = async (eventId, text) => {
  const res = await axios.post(`${API_URL}/api/events/${eventId}/comments`, { text });
  return res.data;
};

export const uploadEventPhoto = async (eventId, file) => {
  const form = new FormData();
  form.append('photo', file);
  const res = await axios.post(`${API_URL}/api/events/${eventId}/photos`, form);
  return res.data;
};

export const uploadMyPhoto = async (file) => {
  const form = new FormData();
  form.append('photo', file);
  const res = await axios.post(`${API_URL}/api/photos/me`, form);
  return res.data;
};

/** Link my (unlinked) account to a person node in the tree. */
export const linkMe = async (personId) => {
  const res = await axios.post(`${API_URL}/api/auth/link-me`, { personId });
  if (res.data.user) {
    const token = getToken();
    if (token) saveSession(token, res.data.user);
  }
  return res.data;
};

/** Add my child to my node (mothers and fathers alike). */
export const addMyChild = async ({ name, gender, birthDate, otherParentId }) => {
  const res = await axios.post(`${API_URL}/api/family/child`, {
    name, gender,
    birthDate: birthDate || undefined,
    otherParentId: otherParentId || undefined,
  });
  return res.data;
};

/** Edit how my own name and details appear. */
export const updateMyDetails = async ({ name, birthDate }) => {
  const res = await axios.put(`${API_URL}/api/family/me`, {
    name: name !== undefined ? name : undefined,
    birthDate: birthDate !== undefined ? birthDate : undefined,
  });
  return res.data;
};

/* ================= Chat ================= */

export const getConversations = async () => {
  const res = await axios.get(`${API_URL}/api/chat/conversations`);
  return res.data;
};

export const getMessages = async (room, before) => {
  const res = await axios.get(`${API_URL}/api/chat/messages/${encodeURIComponent(room)}`, {
    params: { before: before || undefined },
  });
  return res.data;
};

export const getChatMembers = async () => {
  const res = await axios.get(`${API_URL}/api/chat/members`);
  return res.data;
};

export const getRoomMembers = async (room) => {
  const res = await axios.get(`${API_URL}/api/chat/rooms/${encodeURIComponent(room)}/members`);
  return res.data;
};

export const getRoomReceipts = async (room) => {
  const res = await axios.get(`${API_URL}/api/chat/rooms/${encodeURIComponent(room)}/receipts`);
  return res.data.receipts;
};

/* ================= Market & Talents ================= */

export const listMarket = async (kind) => {
  const res = await axios.get(`${API_URL}/api/market`, { params: { kind: kind || undefined } });
  return res.data;
};

export const createListing = async (data) => {
  const res = await axios.post(`${API_URL}/api/market`, data);
  return res.data;
};

export const deleteListing = async (id) => {
  const res = await axios.delete(`${API_URL}/api/market/${id}`);
  return res.data;
};

export const uploadListingPhoto = async (id, file) => {
  const form = new FormData();
  form.append('photo', file);
  const res = await axios.post(`${API_URL}/api/market/${id}/photos`, form);
  return res.data;
};

/** Editors set/replace the photo of any person (elders, the departed). */
export const uploadPersonPhoto = async (personId, file) => {
  const form = new FormData();
  form.append('photo', file);
  const res = await axios.post(`${API_URL}/api/photos/person/${personId}`, form);
  return res.data;
};

/* ================= Living history ================= */

export const getStories = async () => {
  const res = await axios.get(`${API_URL}/api/stories`);
  return res.data;
};

export const getPendingStories = async () => {
  const res = await axios.get(`${API_URL}/api/stories`, { params: { pending: 1 } });
  return res.data;
};

export const submitStory = async (data) => {
  const res = await axios.post(`${API_URL}/api/stories`, data);
  return res.data;
};

export const reviewStory = async (storyId, action) => {
  const res = await axios.put(`${API_URL}/api/stories/${storyId}/review`, { action });
  return res.data;
};

/* ================= Chat groups ================= */

export const createGroup = async (name, memberIds) => {
  const res = await axios.post(`${API_URL}/api/chat/groups`, { name, memberIds });
  return res.data;
};

export const getGroupInfo = async (groupId) => {
  const res = await axios.get(`${API_URL}/api/chat/groups/${groupId}`);
  return res.data;
};

export const updateGroupMembers = async (groupId, { add, remove }) => {
  const res = await axios.put(`${API_URL}/api/chat/groups/${groupId}/members`, { add, remove });
  return res.data;
};

export const leaveGroup = async (groupId) => {
  const res = await axios.post(`${API_URL}/api/chat/groups/${groupId}/leave`);
  return res.data;
};

export const deleteGroup = async (groupId) => {
  const res = await axios.delete(`${API_URL}/api/chat/groups/${groupId}`);
  return res.data;
};

export const getAdminGroups = async () => {
  const res = await axios.get(`${API_URL}/api/chat/groups/all`);
  return res.data;
};

/* ================= Notifications ================= */

export const getNotificationSettings = async () => {
  const res = await axios.get(`${API_URL}/api/notifications/settings`);
  return res.data;
};

export const setRoomMute = async (room, muted) => {
  const res = await axios.put(`${API_URL}/api/notifications/mute`, { room, muted });
  return res.data;
};

export const setMasterMute = async (muteAllHours) => {
  const res = await axios.put(`${API_URL}/api/notifications/mute`, { muteAllHours });
  return res.data;
};

/* ================= Unread badges ================= */

export const markRoomRead = async (room) => {
  const res = await axios.post(`${API_URL}/api/chat/read/${encodeURIComponent(room)}`);
  return res.data;
};

export const getUnreadTotal = async () => {
  const res = await axios.get(`${API_URL}/api/chat/unread-total`);
  return res.data;
};

export const setTreeEditRights = async (userId, canEditTree) => {
  const res = await axios.put(`${API_URL}/api/auth/users/${userId}/tree-edit`, { canEditTree });
  return res.data;
};

export const uploadChatAttachment = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await axios.post(`${API_URL}/api/chat/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

export const proposeRename = async (personId, proposedName) => {
  const res = await axios.post(`${API_URL}/api/people/propose-rename`, { personId, proposedName });
  return res.data;
};

export const getPendingPersonChanges = async () => {
  const res = await axios.get(`${API_URL}/api/people/pending-changes`);
  return res.data;
};

export const reviewPersonChange = async (changeId, action) => {
  const res = await axios.put(`${API_URL}/api/people/pending-changes/${changeId}/review`, { action });
  return res.data;
};

export const getPendingSpouseAdditions = async () => {
  const res = await axios.get(`${API_URL}/api/people/pending-spouse-additions`);
  return res.data;
};

export const reviewSpouseAddition = async (proposalId, action) => {
  const res = await axios.put(`${API_URL}/api/people/pending-spouse-additions/${proposalId}/review`, { action });
  return res.data;
};
